import React from 'react';
import { findDOMNode } from 'react-dom';
import { isEmpty, omitBy, isFunction } from 'lodash';
import { withTheme } from 'styled-components';
import { Transition } from 'react-transition-group';
import theme from '@cld/theme-helper';
import './modal.theme';
import BaseModalWrapper from './ModalBox';
import BaseModalBackground from './ModalBackground';

const { Provider, Consumer } = React.createContext();

class ModalProvider extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {};

    this.queue = [];
    this.pending = {};
  }

  static defaultProps = {
    BackgroundComponent: BaseModalBackground,
    WrapperComponent: BaseModalWrapper,
  };

  noWrapper = ({ children }) => React.Children.only(children);

  handleModalClose = () => {
    const { afterClose, deactivate } = this.state;

    this.cleanUp();
    isFunction(afterClose) && afterClose();
    deactivate();
    this.getNextItem();
  };

  handleModalOpen = () => {
    const { afterOpen } = this.state;

    document.addEventListener('keydown', this.onKeydown);

    if (!this.props.allowScroll) {
      [this.prevBodyOverflow, document.body.style.overflow] = [document.body.style.overflow, 'hidden'];
    }

    isFunction(afterOpen) && afterOpen();
  };

  getNextItem = () => {
    this.activeModalId = null;
    if (!isEmpty(this.queue)) {
      const modalId = this.queue.shift();
      const modalState = this.pending[modalId];
      delete this.pending[modalId];

      modalState.isOpen ? this.updateState(modalId, modalState) : modalState.deactivate();
    }
  };

  componentWillUnmount() {
    if (this.state.isOpen) {
      this.cleanUp();
    }
  }

  cleanUp = () => {
    document.removeEventListener('keydown', this.onKeydown);

    if (!this.props.allowScroll) {
      [this.prevBodyOverflow, document.body.style.overflow] = [null, this.prevBodyOverflow || ''];
    }

    //overcome z-index issues
    if (this.node) {
      [this.node.style.zIndex, this.zIndex] = [this.zIndex, this.node.style.zIndex, undefined];
      [this.node.style.opacity, this.opacity] = [this.opacity, undefined];
    }
  };

  onKeydown = (e) => {
    const { onEscapeKeydown } = this.state;

    if (e.key === 'Escape' && isFunction(onEscapeKeydown)) {
      onEscapeKeydown(e);
    }
  };

  onBackgroundClick = (e) => {
    const { onBackgroundClick } = this.state;

    if (this.backgroundNode === e.target && isFunction(onBackgroundClick)) {
      onBackgroundClick(e);
    }
  };

  addModal = (modalId, modalState) => {
    const shouldQueue = this.activeModalId && modalId !== this.activeModalId;

    shouldQueue ? this.queueModal(modalId, modalState) : this.updateState(modalId, modalState);
  };

  updateState = (modalId, modalState) => {
    this.activeModalId = modalId;
    this.setState((prevState) => {
      const newState = omitBy(modalState, (value, key) => prevState[key] === value);
      return isEmpty(newState) ? null : newState;
    });
  };

  queueModal = (modalId, modalState) => {
    const isNotQueuedAlready = !this.pending[modalId];

    isNotQueuedAlready && this.queue.push(modalId);
    this.pending[modalId] = modalState;
  };

  setBackgroundRef = (node) => (this.backgroundNode = node);

  renderModal = () => {
    const { modalComponent, isOpen, restModalProps } = this.state;
    if (!modalComponent) {
      return null;
    }

    const { BackgroundComponent, WrapperComponent: recWrapperComponent } = this.props;
    const WrapperComponent = recWrapperComponent || this.noWrapper;
    const transitionDelay = this.props.transitionDelay || theme`components.modal.delay`(this.props) || 0;

    return (
      <Transition
        in={isOpen}
        timeout={transitionDelay}
        mountOnEnter
        unmountOnExit
        appear
        onEntered={this.handleModalOpen}
        onExited={this.handleModalClose}
        onEnter={this.state.beforeOpen}
        onExit={this.state.beforeClose}
      >
        {(state) => (
          <BackgroundComponent
            onClick={this.onBackgroundClick}
            ref={this.setBackgroundRef}
            isOpen={state === 'entering' || state === 'entered'}
            delay={transitionDelay}
          >
            <WrapperComponent {...restModalProps} isOpen={state === 'entering' || state === 'entered'} delay={transitionDelay}>
              {modalComponent}
            </WrapperComponent>
          </BackgroundComponent>
        )}
      </Transition>
    );
  };

  render() {
    return (
      <Provider
        value={{
          addModal: this.addModal,
        }}
      >
        {this.props.children}
        <this.renderModal />
      </Provider>
    );
  }

  componentDidMount() {
    this.node = findDOMNode(this);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.isOpen !== prevState.isOpen && this.state.isOpen) {
      //overcome z-index issues
      if (this.node) {
        [this.zIndex, this.node.style.zIndex] = [this.node.style.zIndex, '0'];
        //this designate the child element as the root of a new local stacking context
        [this.opacity, this.node.style.opacity] = [this.node.style.opacity, '0.99999'];
      }
    }
  }
}

const ModalProviderWithTheme = withTheme(ModalProvider);

export { Consumer, ModalProviderWithTheme as default };
